/**
 * Zustand store for the globally-mounted `RefreshFromDbDialog`.
 *
 * Any surface that needs to open the dialog (Database Blueprint toolbar, mounted
 * folder context menu, command palette, etc.) calls `openRefreshDialog(...)`
 * with the blueprint folder and linked-DB selection. The dialog lives at the
 * App root (see `GlobalRefreshFromDbDialog`) so context menus can launch it
 * without having to host the dialog themselves.
 *
 * On success, `onSuccess` is invoked so the caller can update its own state
 * (e.g. push the new `lastSyncAt` into the script-browser store).
 */

import { create } from 'zustand';
import type {
  BlueprintDataTable,
  BlueprintMetadata,
  UserFacingEngineId,
} from '../../../database-blueprint/types/databaseBlueprintTypes';

export type RefreshFromDbDialogArgs = {
  folderPath: string;
  folderId: string;
  linkLabel: string;
  link: {
    connectionDisplayName: string;
    database: string;
    engine: UserFacingEngineId;
  };
  blueprintDataTables: ReadonlyArray<BlueprintDataTable>;
  onSuccess?: (metadata: BlueprintMetadata) => void;
  /** ConnectionId of the active link — used to render the inline
   *  connection-lost notice when the build fails with a connection error. */
  activeLinkConnectionId?: string;
};

type State = {
  isOpen: boolean;
  args: RefreshFromDbDialogArgs | null;
};

type Actions = {
  openRefreshDialog: (args: RefreshFromDbDialogArgs) => void;
  closeRefreshDialog: () => void;
};

export const useRefreshFromDbDialogStore = create<State & Actions>((set) => ({
  isOpen: false,
  args: null,
  openRefreshDialog: (args) => set({ isOpen: true, args }),
  closeRefreshDialog: () => set({ isOpen: false, args: null }),
}));
